
interface Tool {
  id: number;
  name: string;
  description: string;
  icon: string;
  color?: string;
  features?: string[];
  status?: string;
  image?: string;
  type: string;
  category: string;
  link?: string;
}

interface ToolCardProps {
  tool: Tool;
}

export default function ToolCard({ tool }: ToolCardProps) {
  const getTypeIcon = (type: string) => {
    switch(type) {
      case 'PDF': return 'ri-file-pdf-line';
      case 'Software': return 'ri-computer-line';
      default: return 'ri-file-line';
    }
  };

  const getTypeColor = (type: string) => {
    switch(type) {
      case 'PDF': return 'bg-red-500';
      case 'Software': return 'bg-emerald-600';
      default: return 'bg-gray-500';
    }
  };

  const getActionText = (type: string) => {
    return type === 'Software' ? 'Access Tool' : 'Download';
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 group border border-gray-100">
      {tool.image && (
        <div className="relative h-48 overflow-hidden">
          <img
            src={tool.image}
            alt={tool.name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          <div className="absolute bottom-4 left-4 right-4">
            <span className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white shadow-lg ${getTypeColor(tool.type)}`}>
              <i className={`${getTypeIcon(tool.type)}`}></i>
              <span>{tool.type}</span>
            </span>
          </div>
        </div>
      )}

      {!tool.image && (
        <div className={`relative h-40 bg-gradient-to-br ${tool.color || 'from-emerald-500 to-emerald-600'} flex items-center justify-center`}>
          <i className={`${tool.icon} text-6xl text-white opacity-90`}></i>
          <div className="absolute top-4 right-4">
            <span className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white shadow-lg ${getTypeColor(tool.type)}`}>
              <i className={`${getTypeIcon(tool.type)}`}></i>
              <span>{tool.type}</span>
            </span>
          </div>
        </div>
      )}

      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
          {tool.name}
        </h3>
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {tool.description}
        </p>

        {tool.features && tool.features.length > 0 && (
          <div className="mb-4 pb-4 border-b border-gray-100">
            <ul className="space-y-2">
              {tool.features.slice(0, 3).map((feature, index) => (
                <li key={index} className="flex items-start space-x-2 text-sm text-gray-700">
                  <i className="ri-check-line text-emerald-600 mt-0.5 flex-shrink-0 font-bold"></i>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex gap-2">
          <button className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition-colors text-center whitespace-nowrap cursor-pointer flex items-center justify-center space-x-2">
            <i className={`${getTypeIcon(tool.type)}`}></i>
            <span>{getActionText(tool.type)}</span>
          </button>
          <button className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center">
            <i className="ri-share-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
